// src/components/Sidebar.js
import React, { useEffect, useState, useMemo } from 'react'
import {
  LayoutDashboard,
  Users,
  UserCog,
  SprayCan,
  Wrench,
  ClipboardList,
  MapPin,
  Wallet,
  FileText,
  BarChart3,
  CarFront,
  Settings,
  LogOut,
  MessageCircle,
  ShieldCheck,
  Key,
} from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '../firebaseConfig'
import { usePendingMissionClaims } from '../hooks/usePendingMissionClaims'

/**
 * Sidebar component (full-featured)
 *
 * Props:
 * - user: current admin user object (must include .role and .adminName ideally)
 * - onLogout: function
 * - activeMenu, setActiveMenu (optional, for backwards compatibility)
 *
 * Behaviour:
 * - Reads permissions from Firestore document admin_roles/{role}
 * - If not available: fallback (super_admin => all true)
 * - Renders all menu items and hides those not allowed by permission doc
 */

export default function Sidebar({ activeMenu, setActiveMenu, onLogout, user }) {
  const pendingMissionCount = usePendingMissionClaims()
  const location = useLocation()
  const navigate = useNavigate()
  const currentPath = location.pathname

  const role = user?.role || 'unknown'
  const adminName = user?.adminName || user?.email || 'Admin'

  const [loadingPerms, setLoadingPerms] = useState(true)
  const [perms, setPerms] = useState(null) // will hold object from admin_roles/{role}

  // default permission sets (conservative)
  const defaultPerms = useMemo(() => {
    return {
      // all menu keys used in sidebar must be here
      dashboard: false,
      users: false,
      therapists: false,
      therapist_services: false,
      cleaners: false,
      cleaner_services: false,
      admin_management: false,
      area_coverage: false,
      finance: false,
      orders: false,
      reports: false,
      drivers: false,
      inbox: false,
      settings: false,
    }
  }, [])

  // super_admin override: everything allowed
  const superAdminAllTrue = useMemo(() => {
    const obj = {}
    Object.keys(defaultPerms).forEach((k) => (obj[k] = true))
    return obj
  }, [defaultPerms])

  useEffect(() => {
    let mounted = true
    async function loadPerms() {
      setLoadingPerms(true)
      try {
        if (!role) {
          setPerms(defaultPerms)
          setLoadingPerms(false)
          return
        }

        const ref = doc(db, 'admin_roles', role)
        const snap = await getDoc(ref)
        if (!mounted) return

        if (!snap.exists()) {
          // fallback: if role === 'super_admin' assume full access, else conservative
          if (role === 'super_admin') {
            setPerms(superAdminAllTrue)
          } else {
            setPerms(defaultPerms)
          }
          setLoadingPerms(false)
          return
        }

        const data = snap.data() || {}
        // Merge with defaultPerms to ensure all keys exist
        const merged = { ...defaultPerms, ...data }
        setPerms(merged)
      } catch (err) {
        console.error('Sidebar: failed loading admin_roles:', err)
        // fallback safe
        setPerms(role === 'super_admin' ? superAdminAllTrue : defaultPerms)
      } finally {
        setLoadingPerms(false)
      }
    }

    loadPerms()
    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role])

  // helper to check access
  function can(menuKey) {
    if (loadingPerms) return false
    if (!perms) return false
    // permissive if field true
    return Boolean(perms[menuKey])
  }

  // small UI helpers
  const btnClass = (isActive) =>
    `w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-sm ${
      isActive
        ? 'bg-blue-600 text-white'
        : 'text-gray-300 hover:bg-gray-800 hover:text-white'
    }`

  // If user prop missing, show fallback sidebar skeleton with limited items
  if (!user) {
    return (
      <aside className="w-64 bg-gray-900 border-r border-gray-800 flex flex-col">
        <div className="p-5 border-b border-gray-800">
          <h1 className="text-xl font-bold text-white tracking-wide">
            🧭 Zimo Admin
          </h1>
          <p className="text-xs text-gray-500 mt-1">Loading user...</p>
        </div>
        <nav className="flex-1 p-3">
          <div className="animate-pulse h-6 bg-gray-800 rounded mb-3" />
          <div className="animate-pulse h-6 bg-gray-800 rounded mb-3" />
          <div className="animate-pulse h-6 bg-gray-800 rounded mb-3" />
        </nav>
        <div className="p-3 border-t border-gray-800">
          <button className="w-full px-3 py-2 text-sm rounded bg-red-600 text-white">
            Logout
          </button>
        </div>
      </aside>
    )
  }

  return (
    <aside className="w-64 bg-gray-900 border-r border-gray-800 flex flex-col">
      {/* HEADER */}
      <div className="p-5 border-b border-gray-800">
        <h1 className="text-xl font-bold text-white tracking-wide">
          🧭 Zimo Admin
        </h1>
        <p className="text-xs text-gray-500 mt-1">
          {adminName}
          <br />
          <span className="text-blue-400">{role}</span>
        </p>
        {/* show loading perms */}
        {loadingPerms ? (
          <p className="text-xs text-yellow-300 mt-2">Loading permissions…</p>
        ) : null}
      </div>

      {/* MENU */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {/* Dashboard */}
        {can('dashboard') && (
          <button
            onClick={() => navigate('/')}
            className={btnClass(currentPath === '/')}
          >
            <LayoutDashboard size={18} />
            <span>Dashboard</span>
          </button>
        )}

        {/* Users */}
        {can('users') && (
          <button
            onClick={() => navigate('/users')}
            className={btnClass(currentPath.startsWith('/users'))}
          >
            <Users size={18} />
            <span>Users</span>
          </button>
        )}

        {/* Therapists */}
        {can('therapists') && (
          <button
            onClick={() => navigate('/therapists')}
            className={btnClass(currentPath.startsWith('/therapists'))}
          >
            <UserCog size={18} />
            <span>Therapists</span>
          </button>
        )}

        {/* Therapist Services */}
        {can('therapist_services') && (
          <button
            onClick={() => navigate('/therapist-services')}
            className={btnClass(currentPath === '/therapist-services')}
          >
            <ClipboardList size={18} />
            <span>Terapis — Services</span>
          </button>
        )}

        {/* Cleaners */}
        {can('cleaners') && (
          <button
            onClick={() => navigate('/cleaners')}
            className={btnClass(currentPath.startsWith('/cleaners'))}
          >
            <SprayCan size={18} />
            <span>Cleaners</span>
          </button>
        )}

        {/* Cleaner Services */}
        {can('cleaner_services') && (
          <button
            onClick={() => navigate('/cleaner-services')}
            className={btnClass(currentPath === '/cleaner-services')}
          >
            <Wrench size={18} />
            <span>Cleaner — Services</span>
          </button>
        )}

        {/* Admin Management */}
        {can('admin_management') && (
          <button
            onClick={() => navigate('/admin-management')}
            className={btnClass(
              currentPath.startsWith('/admin-management') ||
                currentPath.startsWith('/admins')
            )}
          >
            <ShieldCheck size={18} />
            <span>Admin Management</span>
          </button>
        )}

        {/* Area Coverage */}
        {can('area_coverage') && (
          <button
            onClick={() => navigate('/area-coverage')}
            className={btnClass(currentPath === '/area-coverage')}
          >
            <MapPin size={18} />
            <span>Area Coverage</span>
          </button>
        )}

        {/* Finance */}
        {can('finance') && (
          <button
            onClick={() => navigate('/finance')}
            className={btnClass(currentPath === '/finance')}
          >
            <Wallet size={18} />
            <span>Finance</span>
          </button>
        )}

        {/* Orders */}
        {can('orders') && (
          <button
            onClick={() => navigate('/orders')}
            className={btnClass(currentPath.startsWith('/orders'))}
          >
            <FileText size={18} />
            <span>Orders</span>
          </button>
        )}

        {/* Reports */}
        {can('reports') && (
          <button
            onClick={() => navigate('/reports')}
            className={btnClass(currentPath === '/reports')}
          >
            <BarChart3 size={18} />
            <span>Reports</span>
          </button>
        )}

        {/* Drivers */}
        {can('drivers') && (
          <button
            onClick={() => navigate('/drivers')}
            className={btnClass(currentPath === '/drivers')}
          >
            <CarFront size={18} />
            <span>Drivers</span>
          </button>
        )}

        {/* Chat / Inbox */}
        {can('inbox') && (
          <button
            onClick={() => navigate('/inbox')}
            className={btnClass(currentPath.startsWith('/inbox'))}
          >
            <MessageCircle size={18} />
            <span>Chat & Notifikasi</span>
          </button>
        )}

        {/* Settings */}
        {can('settings') && (
          <button
            onClick={() => navigate('/settings')}
            className={btnClass(currentPath === '/settings')}
          >
            <Settings size={18} />
            <span>Settings</span>

            {pendingMissionCount > 0 && (
              <span className="ml-auto bg-red-600 text-xs px-2 py-0.5 rounded-full">
                {pendingMissionCount}
              </span>
            )}
          </button>
        )}
      </nav>

      {/* LOGOUT */}
      <div className="p-3 border-t border-gray-800">
        <button
          onClick={() => {
            if (onLogout) onLogout()
          }}
          className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition"
        >
          <LogOut size={16} />
          Logout
        </button>
      </div>
    </aside>
  )
}
